import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

pdfMake.addVirtualFileSystem(pdfFonts);

/**
 * Konwertuje obraz do base64
 */
async function getBase64Image(url) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = function() {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            const dataURL = canvas.toDataURL('image/png');
            resolve(dataURL);
        };
        img.onerror = reject;
        img.src = url;
    });
}

/**
 * Formatuje datę do formatu DD.MM.YYYY
 */
function formatDate(dateString) {
    if (!dateString) return '—';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
}

/**
 * Zwraca klasyfikację słowną na podstawie sumy punktów (skala /15)
 */
function getQualificationLabel(totalScore) {
    if (totalScore >= 12) return 'Kwalifikowany';
    if (totalScore >= 9)  return 'Warunkowo kwalifikowany';
    return 'Niekwalifikowany';
}

/**
 * Oblicza średnią tablicy liczb, zaokrągloną do 1 miejsca po przecinku
 */
function avg(arr) {
    if (!arr.length) return 0;
    return Math.round((arr.reduce((a, b) => a + b, 0) / arr.length) * 10) / 10;
}

/**
 * Generuje dokument PDF z raportem kwartalnym oceny dostawcy
 * @param {Object} customer    - Dane dostawcy/klienta
 * @param {Array}  evaluations - Lista ocen dostaw z danego kwartału (z wypełnionym polem `order`)
 * @param {number} year        - Rok raportu
 * @param {number} quarter     - Kwartał raportu (1–4)
 * @param {Array}  qualifications - Lista kwalifikacji dostawcy dla danego kwartału
 * @param {Function} getUserName - Funkcja (userId) => string
 */
export async function generateSupplierQuarterlyReportPDF(customer, evaluations, year, quarter, qualifications, getUserName) {
    // Załaduj logo jako base64
    let logoBase64 = null;
    try {
        logoBase64 = await getBase64Image('/marss-logo.png');
    } catch (error) {
        console.warn('Nie udało się załadować logo:', error);
    }

    // ── Statystyki zbiorcze ──────────────────────────────────────────────────
    const qualityScores  = evaluations.map(e => e.rating.quality);
    const deliveryScores = evaluations.map(e => e.rating.delivery);
    const priceScores    = evaluations.map(e => e.rating.price);
    const totalScores    = evaluations.map(e => e.totalScore);

    const avgQuality  = avg(qualityScores);
    const avgDelivery = avg(deliveryScores);
    const avgPrice    = avg(priceScores);
    const avgTotal    = avg(totalScores);

    const categories = [...new Set(evaluations.map(e => e.goodsCategory))].filter(Boolean);
    // ── Formalna decyzja kwalifikacyjna ─────────────────────────────────────────────
    const formalQualification = qualifications && qualifications.length > 0 ? qualifications[0] : null;

    const qualificationDecisionTableBody = [
        [
            { text: 'Decyzja kwalifikacyjna',          style: 'tableHeader' },
            { text: 'Kto zakwalifikował',              style: 'tableHeader' },
            { text: 'Data decyzji',                    style: 'tableHeader', alignment: 'center' },
            { text: 'Komentarz',                       style: 'tableHeader' }
        ],
        formalQualification ? [
            {
                text: formalQualification.qualified ? 'Zakwalifikowany' : 'Niezakwalifikowany',
                style: 'tableCell',
                bold: true,
                color: formalQualification.qualified ? '#1a7c1a' : '#b00000'
            },
            { text: getUserName(formalQualification.createdBy), style: 'tableCell' },
            { text: formatDate(formalQualification.createdAt),  style: 'tableCell', alignment: 'center' },
            { text: formalQualification.comments || '—',         style: 'tableCell' }
        ] : [
            { text: 'Brak wpisu kwalifikacji dla tego kwartału', style: 'tableCell', colSpan: 4, color: '#888888', italics: true },
            {}, {}, {}
        ]
    ];
    // ── Tabela podsumowująca ─────────────────────────────────────────────────
    const summaryTableBody = [
        [
            { text: 'Kryterium oceny',                                  style: 'tableHeader' },
            { text: 'Ocena (1–5)',                                       style: 'tableHeader', alignment: 'center' },
            { text: 'Waga',                                              style: 'tableHeader', alignment: 'center' },
            { text: 'Opis',                                              style: 'tableHeader' }
        ],
        [
            { text: 'Jakość dostarczanych wyrobów/usług',                style: 'tableCell' },
            { text: avgQuality.toString(),                               style: 'tableCell', alignment: 'center', bold: true },
            { text: '1/3',                                               style: 'tableCell', alignment: 'center' },
            { text: 'Zgodność z wymaganiami, brak reklamacji',           style: 'tableCell' }
        ],
        [
            { text: 'Terminowość realizacji dostaw wyrobów/usług',       style: 'tableCell' },
            { text: avgDelivery.toString(),                              style: 'tableCell', alignment: 'center', bold: true },
            { text: '1/3',                                               style: 'tableCell', alignment: 'center' },
            { text: 'Dotrzymywanie uzgodnionych terminów dostaw',        style: 'tableCell' }
        ],
        [
            { text: 'Proponowane warunki płatności',                     style: 'tableCell' },
            { text: avgPrice.toString(),                                 style: 'tableCell', alignment: 'center', bold: true },
            { text: '1/3',                                               style: 'tableCell', alignment: 'center' },
            { text: 'Warunki cenowe i płatnicze',                        style: 'tableCell' }
        ],
        [
            { text: 'Suma punktów',                                      style: 'tableCell', bold: true },
            {
                text: `${avgTotal}/15`,
                style: 'tableCell',
                alignment: 'center',
                bold: true,
                colSpan: 3
            },
            {},
            {}
        ],
        [
            { text: 'Kwalifikacja dostawcy',                             style: 'tableCell', bold: true },
            {
                text: getQualificationLabel(avgTotal),
                style: 'tableCell',
                alignment: 'center',
                bold: true,
                colSpan: 3,
                color: avgTotal >= 12 ? '#1a7c1a' : avgTotal >= 9 ? '#b06a00' : '#b00000'
            },
            {},
            {}
        ]
    ];

    // ── Tabela szczegółowych ocen ────────────────────────────────────────────
    const detailsTableBody = [
        [
            { text: 'Lp',         style: 'tableHeader', alignment: 'center' },
            { text: 'Nr zamówienia', style: 'tableHeader', alignment: 'center' },
            { text: 'Kategoria',   style: 'tableHeader' },
            { text: 'Data dostawy', style: 'tableHeader', alignment: 'center' },
            { text: 'Jakość',      style: 'tableHeader', alignment: 'center' },
            { text: 'Terminowość', style: 'tableHeader', alignment: 'center' },
            { text: 'Cena',        style: 'tableHeader', alignment: 'center' },
            { text: 'Suma',        style: 'tableHeader', alignment: 'center' },
            { text: 'Kwalifikacja', style: 'tableHeader', alignment: 'center' },
            { text: 'Komentarze',  style: 'tableHeader' },
            { text: 'Oceniający',  style: 'tableHeader' }
        ]
    ];

    evaluations.forEach((evaluation, idx) => {
        detailsTableBody.push([
            { text: (idx + 1).toString(),                               style: 'tableCell', alignment: 'center' },
            { text: evaluation.order?.documentNo || '—',                style: 'tableCell', alignment: 'center' },
            { text: evaluation.goodsCategory || '—',                    style: 'tableCell' },
            { text: formatDate(evaluation.deliveryDate),                 style: 'tableCell', alignment: 'center' },
            { text: evaluation.rating.quality.toString(),               style: 'tableCell', alignment: 'center' },
            { text: evaluation.rating.delivery.toString(),              style: 'tableCell', alignment: 'center' },
            { text: evaluation.rating.price.toString(),                 style: 'tableCell', alignment: 'center' },
            { text: `${evaluation.totalScore}/15`,                      style: 'tableCell', alignment: 'center', bold: true },
            { text: getQualificationLabel(evaluation.totalScore),       style: 'tableCell', alignment: 'center', fontSize: 7 },
            { text: evaluation.comments || '—',                         style: 'tableCell' },
            { text: getUserName(evaluation.createdBy),                  style: 'tableCell' }
        ]);
    });

    // ── Definicja dokumentu ──────────────────────────────────────────────────
    const docDefinition = {
        pageSize: 'A4',
        pageOrientation: 'landscape',
        pageMargins: [30, 60, 30, 40],

        header: {
            columns: [
                { text: `Raport kwartalny – ${customer.name || '—'}`, style: 'headerText', margin: [30, 15, 0, 0] },
                { text: `Q${quarter} / ${year}`, style: 'headerText', alignment: 'right', margin: [0, 15, 30, 0] }
            ]
        },

        footer: function(currentPage, pageCount) {
            return {
                columns: [
                    { text: `Wygenerowano: ${formatDate(new Date())}`, style: 'footerText', margin: [30, 0, 0, 0] },
                    { text: `Strona ${currentPage} z ${pageCount}`, style: 'footerText', alignment: 'right', margin: [0, 0, 30, 0] }
                ]
            };
        },

        content: [
            // Logo
            ...(logoBase64 ? [{
                image: logoBase64,
                width: 120,
                alignment: 'center',
                margin: [0, 0, 0, 10]
            }] : []),

            // Tytuł
            {
                text: 'RAPORT KWARTALNY OCENY DOSTAWCY',
                style: 'title',
                alignment: 'center',
                margin: [0, 0, 0, 4]
            },
            {
                text: `${customer.name || '—'} — Q${quarter} / ${year}`,
                style: 'subtitle',
                alignment: 'center',
                margin: [0, 0, 0, 15]
            },

            // Dane dostawcy
            {
                table: {
                    widths: ['33%', '33%', '34%'],
                    body: [
                        [
                            {
                                stack: [
                                    { text: 'Dane dostawcy', style: 'sectionHeader', margin: [0, 0, 0, 4] },
                                    { text: customer.name || '—',                                   style: 'infoText' },
                                    { text: `${customer.street || '—'}, ${customer.postCode || ''} ${customer.city || ''}`.trim(), style: 'infoText' }
                                ]
                            },
                            {
                                stack: [
                                    { text: 'Kontakt', style: 'sectionHeader', margin: [0, 0, 0, 4] },
                                    { text: customer.phone  || '—', style: 'infoText' },
                                    { text: customer.email  || '—', style: 'infoText' }
                                ]
                            },
                            {
                                stack: [
                                    { text: 'Kategorie dostaw', style: 'sectionHeader', margin: [0, 0, 0, 4] },
                                    { text: categories.join(', ') || '—', style: 'infoText' },
                                    { text: `Liczba ocenionych dostaw: ${evaluations.length}`, style: 'infoText', margin: [0, 4, 0, 0] }
                                ]
                            }
                        ]
                    ]
                },
                layout: {
                    hLineWidth: () => 1,
                    vLineWidth: () => 1,
                    hLineColor: () => '#cccccc',
                    vLineColor: () => '#cccccc'
                },
                margin: [0, 0, 0, 15]
            },

            // Podsumowanie
            { text: 'PODSUMOWANIE KWARTALNE', style: 'sectionHeader', margin: [0, 0, 0, 5] },
            {
                table: {
                    headerRows: 1,
                    widths: ['*', 80, 60, '*'],
                    body: summaryTableBody
                },
                layout: {
                    hLineWidth: () => 1,
                    vLineWidth: () => 1,
                    hLineColor: () => '#000000',
                    vLineColor: () => '#000000'
                },
                margin: [0, 0, 0, 15]
            },

            // Formalna decyzja kwalifikacyjna
            { text: 'DECYZJA KWALIFIKACYJNA DOSTAWCY', style: 'sectionHeader', margin: [0, 0, 0, 5] },
            {
                table: {
                    headerRows: 1,
                    widths: [100, '*', 80, '*'],
                    body: qualificationDecisionTableBody
                },
                layout: {
                    hLineWidth: () => 1,
                    vLineWidth: () => 1,
                    hLineColor: () => '#000000',
                    vLineColor: () => '#000000'
                },
                margin: [0, 0, 0, 15]
            },

            // Szczegółowe oceny
            { text: 'SZCZEGÓŁOWE OCENY DOSTAW', style: 'sectionHeader', margin: [0, 0, 0, 5] },
            {
                table: {
                    headerRows: 1,
                    widths: [20, 60, 'auto', 55, 30, 35, 25, 30, 60, '*', 60],
                    body: detailsTableBody
                },
                layout: {
                    hLineWidth: () => 1,
                    vLineWidth: () => 1,
                    hLineColor: () => '#000000',
                    vLineColor: () => '#000000'
                },
                margin: [0, 0, 0, 15]
            },

            // Legenda
            {
                text: [
                    { text: 'Skala ocen: ', bold: true },
                    '1–2 = niedostateczny, 3 = dostateczny, 4 = dobry, 5 = bardzo dobry  |  ',
                    { text: 'Kwalifikacja: ', bold: true },
                    '12–15 pkt = kwalifikowany,  9–11 pkt = warunkowo kwalifikowany,  <9 pkt = niekwalifikowany'
                ],
                style: 'footerText',
                margin: [0, 0, 0, 0]
            }
        ],

        styles: {
            title: {
                fontSize: 14,
                bold: true
            },
            subtitle: {
                fontSize: 11,
                color: '#444444'
            },
            sectionHeader: {
                fontSize: 10,
                bold: true
            },
            infoText: {
                fontSize: 9,
                margin: [0, 1, 0, 1]
            },
            tableHeader: {
                bold: true,
                fontSize: 8,
                margin: [3, 3, 3, 3],
                fillColor: '#f0f0f0'
            },
            tableCell: {
                fontSize: 8,
                margin: [3, 3, 3, 3]
            },
            headerText: {
                fontSize: 8,
                color: '#666666'
            },
            footerText: {
                fontSize: 7,
                color: '#888888'
            }
        }
    };

    const safeName = (customer.name || 'dostawca').replace(/[^a-z0-9]/gi, '_');
    const fileName = `raport_kwartalny_${safeName}_${year}_Q${quarter}.pdf`;
    pdfMake.createPdf(docDefinition).download(fileName);
}
